import { useEffect, useMemo, useState } from 'react';
import { TableHeadLabel } from './types';

// ----------------------------------------------------------------------

type BreakpointKey = 'sm' | 'md' | 'lg' | 'xl';

// Export headerType for backwards compatibility
export type headerType = TableHeadLabel;

interface UseDynamicTableHeadersProps<T> {
  tableHeaders: TableHeadLabel[];
  breakpoints?: Partial<Record<BreakpointKey, (keyof T | '')[]>>;
}

const breakpointSizes: Record<BreakpointKey, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280
};

export function useDynamicTableHeaders<T>({
  tableHeaders,
  breakpoints
}: UseDynamicTableHeadersProps<T>): [TableHeadLabel[], Set<keyof T | ''>] {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentBreakpoint = useMemo((): BreakpointKey | null => {
    if (!breakpoints) return null;

    const breakpointKeys: BreakpointKey[] = ['sm', 'md', 'lg', 'xl'];

    const matchingBreakpoint = breakpointKeys.find(
      (bp) => windowWidth < breakpointSizes[bp] && breakpoints[bp]
    );

    return matchingBreakpoint || null;
  }, [windowWidth, breakpoints]);

  const filteredHeaders = useMemo(() => {
    if (!currentBreakpoint || !breakpoints?.[currentBreakpoint]) {
      return tableHeaders;
    }

    const allowedKeys = breakpoints[currentBreakpoint] as string[];
    return tableHeaders.filter((header) => allowedKeys.includes(header.id));
  }, [tableHeaders, currentBreakpoint, breakpoints]);

  const dynamicKeys = useMemo(() => {
    return new Set(filteredHeaders.map((h) => h.id as keyof T | ''));
  }, [filteredHeaders]);

  return [filteredHeaders, dynamicKeys];
}
